import { createContext, FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useDisclosure } from "@chakra-ui/react";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useLocation } from "react-router-dom";
import { osName, browserName, mobileVendor } from "react-device-detect";
import AdblockDetector from "adblock-detector";

import { AuthStatus, NotificationsTypes, RegistrationStatus, RoomInformation } from "../../types";
import { dynamicConfig, localRoutes } from "../../config";
import { removeEmitterIdHeader, removeUser, setEmitterIdHeader } from "../api/helpers/authorization";
import { useNotificationsQuery } from "../cache";
import {
  getFromLocalStorage,
  LocalStorageKeys,
  removeFromLocalStorage,
  setToLocalStorage,
} from "../utils/localStorage";
import { getReferrerParam } from "../utils";
import { serverPath } from "../api/config";
import { setSseEmitterId } from "../api/helpers/apiFunctions/channel";
import { Dialog } from "../components/Dialog";
import { messages } from "../locale";
import { useUser } from "../hooks/useUser";
import { isAvailableTimeNow } from "../utils/availableTime";
import { isLocalHost } from "../utils/isLocalHost";
import { Analytics } from "../../analytics";
import { useSetInvite, useSetParticipantLeftId } from "../data";
import { reportError } from "../utils/reportError";

const baseUrl = dynamicConfig.serverUrl;

export interface NotificationsContextType {
  notificationsData: RoomInformation | undefined;
  clearNotificationsData: () => void;
  connection: boolean;
  hasDisconnected: boolean;
  startLeavingChannel: () => void;
}

export const receivedInvitationsPerPage = 10;

export const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

const acceptedPaths = {
  [localRoutes.getChannel]: true,
  [localRoutes.videoCall]: true,
};

let retries = 0;
let retryTimeout: NodeJS.Timeout;
const retryDelay = 15_000;

export const NotificationsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [connection, setConnection] = useState(false);
  const [hasDisconnected, setHasDisconnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notificationsData, setNotificationsData] = useState<RoomInformation>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLeavingChannel, setIsLeavingChannel] = useState(false);
  const { isActivated, isGhost } = useUser();
  const setInvite = useSetInvite();
  const setParticipantLeftId = useSetParticipantLeftId();

  const startLeavingChannel = useCallback(() => {
    setIsLeavingChannel(true);
  }, []);

  const showTheConnectionLostDialog = useCallback(() => {
    onOpen();
    removeEmitterIdHeader();
  }, [onOpen]);

  const { refetch } = useNotificationsQuery();

  const abortController = useMemo(() => new AbortController(), []);
  useEffect(
    () => () => {
      abortController.abort();
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    const isAvailableTime = isLocalHost || isGhost || isAvailableTimeNow();

    if (!connection && !isLoading && !isLeavingChannel && isActivated && isAvailableTime) {
      setIsLoading(true);
      setHasDisconnected(false);

      const fetchData = async () => {
        const accessToken = getFromLocalStorage(LocalStorageKeys.accessToken);
        const utmData = getFromLocalStorage(LocalStorageKeys.UtmData);
        const savedReferrer = getFromLocalStorage(LocalStorageKeys.referrer);
        const referrer = savedReferrer || getReferrerParam();
        const linkSentBy = getFromLocalStorage(LocalStorageKeys.invitationSentBy);
        let sentBy = "";
        if (linkSentBy) {
          sentBy = `&sentBy=${linkSentBy}`;
        }

        let sessionIdParam = "";
        const sessionId = Analytics.getSessionID();
        if (sessionId) {
          sessionIdParam = `&session_id=${sessionId}`;
        }

        const adBlockOn = AdblockDetector.detect();
        const adBlockParam = `&adBlockOn=${adBlockOn}`;

        Analytics.track("Socket Subscribe", { adBlockOn });
        await fetchEventSource(
          `${baseUrl}${serverPath.notifications}?osName=${osName}&browser=${browserName}&mobileVendor=${mobileVendor}${utmData}${referrer}${sentBy}${sessionIdParam}${adBlockParam}`,
          {
            headers: {
              authorization: `Bearer ${accessToken}`,
            },
            signal: abortController.signal,
            openWhenHidden: true,

            async onopen(response) {
              if (response.ok && response.status === 200) {
                console.log("Connection made ", response);
                retries = 0;
                setConnection(true);
                setIsLoading(false);
                setHasDisconnected(false);
                Analytics.track("Socket Connection Opened");
              } else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
                console.log("Client side error ", response);
                Analytics.track("Socket Connection Did Not Opened", {
                  Status: response.status,
                });
                if (response.status === 401) {
                  if (retries < 3) {
                    retries += 1;
                  } else {
                    removeUser();
                    window.location.href = localRoutes.login + window.location.search;
                  }
                }
              }
            },
            onmessage(message) {
              // console.log("MESSAGE ARRIVE", message);
              if (JSON.stringify(message || "").includes("DISCONNECTED")) {
                removeFromLocalStorage(LocalStorageKeys.invitationSentBy);
                Analytics.track("Socket DISCONNECTED Event");
                setHasDisconnected(true);
              }

              if (message.data) {
                const parsedData = JSON.parse(message.data);
                console.log("MESSAGE DATA", parsedData);

                if (parsedData?.sseEmitterId) {
                  setSseEmitterId(parsedData?.sseEmitterId);
                  setEmitterIdHeader(parsedData?.sseEmitterId);
                  setToLocalStorage(LocalStorageKeys.emitterId, parsedData?.sseEmitterId);
                }

                if (parsedData?.notificationData?.notificationType === NotificationsTypes.ONLINE_FRIENDS) {
                  refetch();
                }

                if (parsedData?.notificationData?.notificationType === NotificationsTypes.INVITATION) {
                  setInvite(parsedData);
                  refetch();
                }

                if (
                  parsedData?.notificationData?.notificationType ===
                    NotificationsTypes.NOTIFICATION_PARTICIPANT_LEFT_CHANNEL &&
                  parsedData?.senderId
                ) {
                  setParticipantLeftId(parsedData?.senderId);
                }

                if (parsedData.eventType === NotificationsTypes.VIDEO_AUTHENTICATION) {
                  setNotificationsData(parsedData.videoAuthenticationResponse);
                }
              }
            },
            onclose() {
              console.log("Connection closed by the server");
              Analytics.track("Socket Connection Closed By Server");
              setHasDisconnected(true);
              showTheConnectionLostDialog();
              retryTimeout = setTimeout(() => {
                setConnection(false);
              }, retryDelay);
            },
            onerror(err) {
              reportError(
                "NotificationsContext.tsx - useEffect.fetchData.fetchEventSource.onerror",
                "There was an error from server",
                err
              );
              Analytics.track("Socket Connection Error From Server", {
                Error: err?.message,
              });
              setHasDisconnected(true);
              showTheConnectionLostDialog();
              retryTimeout = setTimeout(() => {
                setConnection(false);
              }, retryDelay);

              const errorString = err.toString().toLowerCase();
              // we stop lib reconnection when no internet
              // and we have our manual reconnection mechanism for when the internet is back
              if (["network error", "load failed", "failed to fetch"].some((error) => errorString.includes(error))) {
                abortController.abort();
                throw new Error(err);
              }
            },
          }
        );
      };

      fetchData().finally(() => {
        retryTimeout = setTimeout(() => {
          setIsLoading(false);
        }, retryDelay);
      });
    }
  }, [connection, isLoading, isLeavingChannel, isActivated, isGhost]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    retries = 0;

    return () => {
      clearTimeout(retryTimeout);
      removeFromLocalStorage(LocalStorageKeys.invitationSentBy);
    };
  }, []);

  const clearNotificationsData = useCallback(() => {
    setNotificationsData(undefined);
  }, []);

  const value: NotificationsContextType = useMemo(
    () => ({
      notificationsData,
      clearNotificationsData,
      connection,
      hasDisconnected,
      startLeavingChannel,
    }),
    [notificationsData, clearNotificationsData, connection, hasDisconnected, startLeavingChannel]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}

      {!dynamicConfig.isProduction && (
        <Dialog
          id="lost-connection-dialog"
          title={messages.notifications.dialog.title}
          isOpen={isOpen}
          onClose={onClose}
          withoutCancel
          onConfirm={onClose}
        >
          {messages.notifications.dialog.description}
        </Dialog>
      )}
    </NotificationsContext.Provider>
  );
};

export const ControlledNotificationsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const { userAuthStatus, userCompletedRegistration } = useUser();

  if (
    acceptedPaths[pathname] &&
    userAuthStatus === AuthStatus.AUTHENTICATED &&
    userCompletedRegistration === RegistrationStatus.FINISH
  ) {
    return <NotificationsProvider>{children}</NotificationsProvider>;
  }

  removeEmitterIdHeader();
  removeFromLocalStorage(LocalStorageKeys.emitterId);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
